/**
 * Renderer, camera rig, and the frame loop.
 *
 * Camera choice matters more than anything else here. The approved reference is
 * flat top-down 2D illustration. A perspective camera immediately breaks that
 * read: verticals converge, parallel furniture edges splay, and the eye stops
 * believing it is looking at a drawing. An ORTHOGRAPHIC camera at a fixed
 * dimetric tilt keeps every parallel line parallel — so the image still reads as
 * illustration — while the scene underneath is genuinely 3D, with real occlusion,
 * real cast shadows, and real physics depth. That contradiction is the whole
 * "2.5D" brief.
 *
 * Lighting lives in `render/lighting.ts` and the image treatment in
 * `render/postfx.ts`. This file owns the wiring: it builds both, feeds them the
 * camera each frame, and — critically — keeps working if either fails. A driver
 * that cannot give us a float render target must still get a playable game, so
 * the post stack is strictly optional and the frame loop falls back to a plain
 * forward render the moment it misbehaves.
 *
 * Query-string escape hatches, for A/B grabs and low-power debugging:
 *   ?fx=off          disable post-processing entirely
 *   ?fx=<a,b,c>      enable only these effects (bloom,ink,grade,vignette,grain)
 *   ?shadows=off     disable shadow casting
 *   ?quality=low|medium|high   pin a quality tier and disable the watchdog
 *   ?quality=auto    force the auto-detect + watchdog path (the default)
 *   ?debug=outline   drop the outline verification rig into the scene
 */
import {
  ACESFilmicToneMapping,
  Timer,
  Fog,
  Group,
  NoToneMapping,
  Object3D,
  OrthographicCamera,
  PCFShadowMap,
  Scene,
  SRGBColorSpace,
  Vector3,
  WebGLRenderer,
} from 'three';
import { srgb } from '../render/palette';
import { LightingRig, type MoodId } from '../render/lighting';
import { createPostFx, type EffectName, type PostFx } from '../render/postfx';
import { enableInkLayer, makeOutlineTestRig, disposeTree, setInkScale } from '../render/cel';
// Importing the texture forge here does two jobs beyond `setForgeAnisotropy`:
// it is the only module that owns a renderer and can therefore report the GPU's
// real anisotropy ceiling, and evaluating the module installs the lazy
// `window.__forgeDemo()` console handle on every route.
import { setForgeAnisotropy } from '../render/textures';

export interface EngineOptions {
  canvas: HTMLCanvasElement;
  /** World-units of vertical extent the camera frames. Drives ortho zoom. */
  viewHeight?: number;
  /** Tilt from straight-down, in degrees. 0 = pure top-down, 90 = side-on. */
  tiltDeg?: number;
  /** Rotation around the world Y axis, in degrees. */
  yawDeg?: number;
  /** Starting lighting + grade mood. */
  mood?: MoodId;
  /** Set false to skip the post-processing stack entirely. */
  postProcessing?: boolean;
}

/** Distance the camera sits from its target. Arbitrary under ortho, but the
 *  near/far planes below are derived from it, so it is not free to change. */
const CAMERA_DISTANCE = 60;

const ALL_EFFECTS: readonly EffectName[] = ['bloom', 'ink', 'grade', 'vignette', 'grain'];

/* ==================================================================== *
 * Quality tiers
 * ==================================================================== */

export type QualityTier = 'low' | 'medium' | 'high';

const TIER_ORDER: readonly QualityTier[] = ['low', 'medium', 'high'];

export interface QualityProfile {
  /**
   * Ceiling on `devicePixelRatio`. This is the single biggest fill-rate lever
   * in the whole engine and the old value was wrong: capping at 2 on a 1.75-DPR
   * laptop rendered 3.06x the pixels of the CSS resolution, and every one of
   * them ran the cel shader, the ink shell, and the whole post chain. The
   * renderer already has MSAA on, and the art is flat colour under a thick
   * contour — the exact content that gains least from supersampling — so even
   * `high` stops at 1.5.
   */
  pixelRatio: number;
  shadows: boolean;
  /** Square shadow-map edge. 2048 is 4x the fill of 1024 for one key light. */
  shadowMapSize: number;
  /** Ceiling on texture anisotropy. Applied once, before any scene mounts. */
  anisotropy: number;
  /** Post effects pinned ON at this tier. Everything else is pinned OFF. */
  effects: readonly EffectName[];
}

/**
 * Three tiers, chosen against the measured cost of each lever rather than
 * against vibes.
 *
 * `ink` — the interior line pass — is a **tier lever, not a global switch**. It
 * used to be pinned off everywhere on the strength of a 93x frame-time penalty
 * measured on an Intel Arc iGPU. That number was a hardware cliff, not an
 * algorithmic defect: re-measured on an RTX 3060 at 1600x900 with `gl.finish()`
 * per frame it costs 0.8 ms, a 1.64x ratio, which is *cheaper* than the extra
 * geometry pass would predict because the `NormalDepthBuffer` re-render is
 * depth/normal only and does almost no shading (docs/PERF.md section 0).
 *
 * So it lives on `high` alone, and that placement is deliberate on both sides:
 *
 * - **It is on at all** because interior seams are an approved art-direction
 *   requirement, not polish. Inverted-hull outlines can only ever draw a
 *   silhouette; the crease where a cushion meets an arm has to come from here.
 * - **It stops at `high`** so the watchdog's very first demotion sheds it. Ink
 *   is ~40% of the frame on the reference GPU, which makes one tier step a
 *   meaningful recovery instead of a token one — and it means an Arc-class part
 *   (already scored down to `low`/`medium` by `detectTier`) never renders the
 *   pass that fell off its driver's fast path in the first place.
 *
 * Promoting `ink` to `medium` is a real option once a mid-tier part has actually
 * been measured. Until then this is the placement the evidence supports.
 */
export const QUALITY_PROFILES: Record<QualityTier, QualityProfile> = {
  low: {
    pixelRatio: 1,
    // Worth 104 of Game 1's 241 draw calls and 62k of its 147k triangles: the
    // shadow pass re-renders every caster. Dropping it is the largest single
    // saving available without touching scene content.
    shadows: false,
    shadowMapSize: 512,
    anisotropy: 1,
    effects: ['grade', 'vignette'],
  },
  medium: {
    pixelRatio: 1.25,
    shadows: true,
    shadowMapSize: 1024,
    anisotropy: 4,
    effects: ['bloom', 'grade', 'vignette', 'grain'],
  },
  high: {
    pixelRatio: 1.5,
    shadows: true,
    shadowMapSize: 2048,
    anisotropy: 8,
    effects: ['bloom', 'ink', 'grade', 'vignette', 'grain'],
  },
};

/**
 * First guess at a tier, before the watchdog has any evidence.
 *
 * Deliberately pessimistic. Starting low and being promoted after a second of
 * good frames is invisible; starting high and stuttering for a second while the
 * watchdog notices is the first thing the player sees.
 */
function detectTier(renderer: WebGLRenderer): QualityTier {
  try {
    return detectTierUnsafe(renderer);
  } catch {
    // Any missing browser API (SSR, jsdom, a locked-down context) lands here.
    // `medium` is the safe answer: the watchdog corrects it either way.
    return 'medium';
  }
}

function detectTierUnsafe(renderer: WebGLRenderer): QualityTier {
  let score = 0;

  const cores = navigator.hardwareConcurrency ?? 4;
  score += cores >= 12 ? 2 : cores >= 8 ? 1 : cores >= 4 ? 0 : -1;

  const mem = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  if (mem !== undefined) score += mem >= 8 ? 1 : mem >= 4 ? 0 : -1;

  // Total pixels we would have to fill at the high-tier ratio. A 4K panel on a
  // modest GPU is a worse combination than a 1080p panel on the same GPU.
  const px = window.screen.width * window.screen.height * Math.min(window.devicePixelRatio, 1.5) ** 2;
  score += px > 6e6 ? -1 : px < 2.5e6 ? 1 : 0;

  // Coarse pointer plus a small viewport is the phone/tablet signature.
  if (matchMedia('(pointer: coarse)').matches && Math.min(window.screen.width, window.screen.height) < 900) {
    score -= 2;
  }

  // The renderer string is unmasked on most desktop browsers and is the only
  // direct evidence about the GPU we get. Software rasterisers and the
  // low-power integrated parts that this project has actually been observed
  // choking on get pinned to `low` outright.
  const gl = renderer.getContext();
  const dbg = gl.getExtension('WEBGL_debug_renderer_info');
  const gpu = (dbg ? String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL)) : '').toLowerCase();
  if (/swiftshader|llvmpipe|software|basic render|microsoft basic/.test(gpu)) return 'low';
  if (/\b(uhd|hd graphics|iris|arc)\b|adreno|mali|powervr|apple gpu/.test(gpu)) score -= 2;
  if (/rtx|radeon rx|geforce (gtx )?(10|16|20|30|40)/.test(gpu)) score += 2;

  return score >= 3 ? 'high' : score >= 1 ? 'medium' : 'low';
}

export class Engine {
  readonly renderer: WebGLRenderer;
  readonly scene: Scene;
  readonly camera: OrthographicCamera;
  /** Move this to pan the camera; the camera orbits it at a fixed offset. */
  readonly cameraTarget: Group;
  /** `Timer`, not `Clock` — `THREE.Clock` is deprecated as of r183 and warns on every construction. */
  readonly clock = new Timer();

  readonly lighting: LightingRig;
  /** Back-compat accessor. Prefer `engine.lighting`. */
  readonly lights: LightingRig['lights'];
  /** `null` when post-processing is unavailable or disabled. */
  readonly postfx: PostFx | null;

  viewHeight: number;
  private tiltDeg: number;
  private yawDeg: number;

  private readonly updaters = new Set<(dt: number, elapsed: number) => void>();
  private running = false;
  private rafId = 0;
  private resizeObserver?: ResizeObserver;
  private windowResizeHandler?: () => void;
  private debugRig?: Group;

  /* per-frame scratch — the frame loop must never allocate */
  private readonly _offset = new Vector3();

  /** Frame-time telemetry, surfaced to the HUD and to the progress page. */
  readonly stats = { fps: 0, frameMs: 0, drawCalls: 0, triangles: 0, tier: 'medium' as QualityTier };
  private fpsAccum = 0;
  private fpsFrames = 0;

  /* ---- quality ---------------------------------------------------- */

  /** Current tier. Change with `setQuality`, or let the watchdog do it. */
  quality: QualityTier = 'medium';
  /** True when `?quality=` pinned a tier, which switches the watchdog off. */
  private qualityPinned = false;
  /** Smoothed frame time in ms. Seeded optimistically so we don't drop at boot. */
  private frameMsEma = 16;
  /** Frames the EMA has been continuously bad / continuously good. */
  private badFrames = 0;
  private goodFrames = 0;
  /** Auto-promotion is allowed at most once, to stop tier oscillation. */
  private promotionsLeft = 1;
  /** `?shadows=off` outranks the tier. */
  private shadowsForcedOff = false;
  /** True while the query string is choosing effects, so tiers must not fight it. */
  private effectsFromQuery = false;

  constructor(opts: EngineOptions) {
    this.viewHeight = opts.viewHeight ?? 12;
    this.tiltDeg = opts.tiltDeg ?? 42;
    this.yawDeg = opts.yawDeg ?? 0;

    const query = readQuery();

    this.renderer = new WebGLRenderer({
      canvas: opts.canvas,
      antialias: true,
      alpha: false,
      powerPreference: 'high-performance',
      stencil: false,
    });
    // Tier first: it owns the pixel-ratio cap, the shadow map size and the
    // texture anisotropy ceiling, and all three have to be right *before*
    // anything sizes a buffer or bakes a surface.
    const wanted = query.quality;
    if (wanted === 'low' || wanted === 'medium' || wanted === 'high') {
      this.quality = wanted;
      this.qualityPinned = true;
    } else {
      this.quality = detectTier(this.renderer);
    }
    this.stats.tier = this.quality;
    const profile = QUALITY_PROFILES[this.quality];

    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, profile.pixelRatio));
    // Flat cel colour must survive to the screen unmodified. Tone mapping would
    // compress exactly the saturated brand reds and greens we chose on purpose.
    this.renderer.toneMapping = NoToneMapping;
    this.renderer.outputColorSpace = SRGBColorSpace;
    this.renderer.shadowMap.enabled = true;
    // PCFSoftShadowMap is deprecated in r185 and silently degrades to PCF.
    // Ask for PCF explicitly: it is what actually runs, and unlike the "soft"
    // variant it honours `shadow.radius`, which the lighting presets set per
    // mood. A painted illustration wants a controllable edge, not a blur.
    this.renderer.shadowMap.type = PCFShadowMap;
    // Several renders happen per frame once the composer is in play; manual
    // reset is the only way `stats.drawCalls` reports the true frame total.
    this.renderer.info.autoReset = false;

    // Baked surfaces are memoised on first use, so this has to land BEFORE any
    // scene mounts or the early textures keep the conservative default. A floor
    // seen down a 42-degree dimetric tilt is the worst case for mip blur, and
    // that floor is most of the frame.
    // Anisotropy is a per-tier ceiling, not a "take whatever the GPU offers".
    // 16x on a floor that fills most of the frame is a real sampling cost, and
    // this is a one-shot decision: baked surfaces are memoised on first use, so
    // a later tier change cannot retroactively re-filter them.
    setForgeAnisotropy(Math.min(this.renderer.capabilities.getMaxAnisotropy(), profile.anisotropy));

    this.scene = new Scene();
    this.scene.background = srgb(0x173721);

    // A tight depth range. The old (-200, 400) frustum wasted almost all depth
    // precision and, worse, let `-viewZ` go negative, which the post stack's
    // linear-depth packing cannot represent.
    this.camera = new OrthographicCamera(-1, 1, 1, -1, 0.1, CAMERA_DISTANCE * 4);
    // Outline shells live on their own layer so the post stack can exclude them.
    enableInkLayer(this.camera);

    this.cameraTarget = new Group();
    this.scene.add(this.cameraTarget);

    this.lighting = new LightingRig(this.scene, opts.mood ?? 'hub');
    this.lights = this.lighting.lights;
    this.shadowsForcedOff = query.shadows === 'off';

    this.applyCameraOrientation();
    this.resize();

    this.postfx =
      opts.postProcessing === false || query.fx === 'off'
        ? null
        : createPostFx({
            renderer: this.renderer,
            scene: this.scene,
            camera: this.camera,
            focus: this.cameraTarget.position,
            mood: opts.mood ?? 'hub',
          });

    if (this.postfx) {
      // ?fx=ink,grain -> only those two run.
      if (query.fx && query.fx !== 'off') {
        this.effectsFromQuery = true;
        const wanted = new Set(query.fx.split(','));
        for (const name of ALL_EFFECTS) this.forceEffect(name, wanted.has(name));
      }
      // No `else` branch on purpose. Every effect — the interior-ink pass
      // included — is decided by `applyQuality()` below, from the tier profile.
      // There used to be an unconditional `forceEffect('ink', false)` here; it
      // is gone because the 93x measurement behind it was an Intel Arc driver
      // cliff, not an algorithmic defect (docs/PERF.md section 0).
      //
      // `applyQuality` pins through `forceEffect`, NOT `setEffectEnabled`. That
      // distinction is the whole reason an earlier version of this fix appeared
      // to work and then silently died: `setMood()` calls `postfx.applyPreset()`,
      // which replaces the entire settings tree and takes any plain
      // `setEffectEnabled` with it. Both scenes call `setMood` in `enter()`, so
      // an unpinned toggle survives exactly until the first scene loads.
      // Forced effects are re-stamped by `reapplyForcedEffects()` on every
      // `setMood`, so this decision outlives every preset change.

      // `resize()` ran before the stack existed, so size its buffers now.
      const size = this.canvasSize();
      this.postfx.setSize(size.w, size.h);
    }

    // Applies shadows, shadow map size and the tier's effect set — including
    // whether the interior-ink pass runs. Runs last so the tier is the final
    // word, and skipped for effects when `?fx=` already spoke.
    this.applyQuality();

    if (query.debug === 'outline') this.addOutlineTestRig();
  }

  /* ------------------------------------------------------------------ *
   * Quality tiers
   * ------------------------------------------------------------------ */

  /**
   * Push the current tier into every subsystem it owns.
   *
   * Idempotent and cheap, so it is safe to call from `setMood` — which it must
   * be, because `LightingRig.applyPreset` restores the preset's shadow map size
   * and would otherwise quietly hand a low-tier machine a 2048x2048 map on
   * every scene change.
   */
  private applyQuality(): void {
    const p = QUALITY_PROFILES[this.quality];
    this.stats.tier = this.quality;

    const shadows = p.shadows && !this.shadowsForcedOff;
    this.lighting.setShadowsEnabled(shadows);
    // Unconditional, including when shadows are off: `setShadowMapSize` also
    // disposes the existing map, so demoting to `low` actually releases the
    // 2048x2048 depth target instead of leaving 16 MB parked in VRAM.
    this.lighting.setShadowMapSize(p.shadowMapSize);
    this.renderer.shadowMap.enabled = shadows;

    // `?fx=` is an explicit debugging instruction and outranks the tier.
    if (this.postfx && !this.effectsFromQuery) {
      const on = new Set(p.effects);
      for (const name of ALL_EFFECTS) this.forceEffect(name, on.has(name));
    }

    // Pixel ratio, and every buffer derived from it. Guarded: `resize()`
    // reallocates every composer target, and `setMood` calls through here on
    // each scene change, so an unconditional resize would churn VRAM for
    // nothing on the overwhelmingly common no-tier-change path.
    const ratio = Math.min(window.devicePixelRatio, p.pixelRatio);
    if (Math.abs(this.renderer.getPixelRatio() - ratio) > 1e-4) this.resize();
  }

  /**
   * Move to a tier explicitly. Pins it, so the watchdog stops second-guessing a
   * choice the player (or a capture harness) made on purpose.
   */
  setQuality(tier: QualityTier, pin = true): void {
    if (pin) this.qualityPinned = true;
    if (tier === this.quality) return;
    this.quality = tier;
    this.applyQuality();
  }

  /**
   * FPS watchdog.
   *
   * The tier guess at boot is a guess; this is the part that is actually
   * correct, because it measures the machine in front of it. An EMA over frame
   * time rather than an instantaneous read, so one long GC or a scene mount
   * cannot demote a healthy machine, and separate counters for "bad" and "good"
   * so the two directions have independent hysteresis.
   *
   * Demotion is fast (about 1.5 s of bad frames) because a stuttering game is
   * unplayable now. Promotion is slow (about 5 s of good frames) and allowed
   * once, because a wrong promotion costs a visible hitch and then a demotion.
   */
  private watchdog(frameMs: number): void {
    if (this.qualityPinned) return;
    // 0.1 weight = roughly a 10-frame window; long enough to ignore a single
    // hitch, short enough to react inside two seconds.
    this.frameMsEma += (frameMs - this.frameMsEma) * 0.1;

    const BAD_MS = 24; // sustained sub-42fps
    const GOOD_MS = 10; // comfortable headroom at 60fps
    const DEMOTE_AFTER = 90;
    const PROMOTE_AFTER = 300;

    if (this.frameMsEma > BAD_MS) {
      this.goodFrames = 0;
      if (++this.badFrames >= DEMOTE_AFTER) {
        this.badFrames = 0;
        // A demotion means the boot guess was wrong; never auto-promote again.
        this.promotionsLeft = 0;
        this.stepTier(-1);
      }
      return;
    }

    this.badFrames = 0;
    if (this.frameMsEma < GOOD_MS && this.promotionsLeft > 0) {
      if (++this.goodFrames >= PROMOTE_AFTER) {
        this.goodFrames = 0;
        this.promotionsLeft--;
        this.stepTier(1);
      }
      return;
    }
    this.goodFrames = 0;
  }

  private stepTier(delta: number): void {
    const i = TIER_ORDER.indexOf(this.quality);
    const next = TIER_ORDER[Math.max(0, Math.min(TIER_ORDER.length - 1, i + delta))];
    if (!next || next === this.quality) return;
    this.quality = next;
    this.applyQuality();
    // The new tier changes frame cost by design; re-seed rather than let the
    // stale EMA immediately trigger the opposite move.
    this.frameMsEma = 16;
  }

  /* ------------------------------------------------------------------ *
   * Camera
   * ------------------------------------------------------------------ */

  /** Position the camera on its orbit around `cameraTarget`. */
  private applyCameraOrientation(): void {
    const tilt = (this.tiltDeg * Math.PI) / 180;
    const yaw = (this.yawDeg * Math.PI) / 180;
    const d = CAMERA_DISTANCE;
    const y = Math.cos(tilt) * d;
    const h = Math.sin(tilt) * d;
    this._offset.set(Math.sin(yaw) * h, y, Math.cos(yaw) * h);

    this.camera.position.copy(this.cameraTarget.position).add(this._offset);
    this.camera.lookAt(this.cameraTarget.position);
    this.camera.updateMatrixWorld();

    // Lights follow the camera yaw so ink shadows keep a consistent direction as
    // the view pans between rooms, and the shadow frustum refits to the new view.
    this.lighting.update(this.camera, this.cameraTarget.position, yaw);
  }

  setCameraOrientation(tiltDeg: number, yawDeg: number): void {
    this.tiltDeg = tiltDeg;
    this.yawDeg = yawDeg;
    this.applyCameraOrientation();
  }

  setViewHeight(h: number): void {
    this.viewHeight = h;
    this.resize();
  }

  private canvasSize(): { w: number; h: number } {
    const canvas = this.renderer.domElement;
    const parent = canvas.parentElement;
    return {
      w: parent?.clientWidth || canvas.clientWidth || 1,
      h: parent?.clientHeight || canvas.clientHeight || 1,
    };
  }

  /** Recompute ortho frustum from the canvas aspect. */
  resize(): void {
    const { w, h } = this.canvasSize();

    // Pixel ratio first: `setSize` derives the drawing buffer from it, and the
    // composer sizes its targets off the drawing buffer.
    this.renderer.setPixelRatio(
      Math.min(window.devicePixelRatio, QUALITY_PROFILES[this.quality].pixelRatio)
    );
    this.renderer.setSize(w, h, false);

    const aspect = w / h;
    const halfH = this.viewHeight / 2;
    const halfW = halfH * aspect;
    this.camera.left = -halfW;
    this.camera.right = halfW;
    this.camera.top = halfH;
    this.camera.bottom = -halfH;
    this.camera.updateProjectionMatrix();
    this.applyCameraOrientation();

    // Tell the ink system how many world units one CSS pixel spans, so every
    // outline shell can resolve its authored weight into a real screen width.
    // CSS pixels, not device pixels: a 5px contour should read the same
    // physical thickness on a retina panel as on a 1x one.
    setInkScale(this.viewHeight / h);

    this.postfx?.setSize(w, h);
  }

  /** Observe the canvas parent so the view tracks layout changes, not just window resizes. */
  observeResize(): void {
    const parent = this.renderer.domElement.parentElement;
    if (!parent || typeof ResizeObserver === 'undefined') {
      this.windowResizeHandler = () => this.resize();
      window.addEventListener('resize', this.windowResizeHandler);
      return;
    }
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(parent);
  }

  /* ------------------------------------------------------------------ *
   * Mood
   * ------------------------------------------------------------------ */

  /**
   * Switch lighting and grade together. Scenes should call this in `enter()`
   * rather than touching individual lights, so hub / home / factory stay
   * recognisably one world with three times of day.
   */
  setMood(mood: MoodId): void {
    this.lighting.applyPreset(mood);
    this.postfx?.applyPreset(mood);
    // A mood preset replaces the WHOLE post settings tree, which silently undoes
    // any deliberate override — the tier's decision about the interior-ink pass
    // among them. Both scenes call setMood() on enter, so without this line the
    // engine's effect choices survive exactly until the first scene loads, which
    // is precisely how an earlier ink fix regressed. Overrides outrank presets.
    this.reapplyForcedEffects();
    // `LightingRig.applyPreset` restores the preset's shadow map size and
    // re-arms `castShadow`, so the tier has to be stamped back on afterwards.
    this.applyQuality();
    this.applyCameraOrientation();
  }

  /**
   * Effects pinned on or off for reasons a preset must not override — currently
   * performance, later a user quality setting.
   */
  private readonly forcedEffects = new Map<EffectName, boolean>();

  private reapplyForcedEffects(): void {
    if (!this.postfx) return;
    for (const [name, on] of this.forcedEffects) this.postfx.setEffectEnabled(name, on);
  }

  /** Pin an effect so mood presets cannot change it. Pass `null` to unpin. */
  forceEffect(name: EffectName, on: boolean | null): void {
    if (on === null) this.forcedEffects.delete(name);
    else {
      this.forcedEffects.set(name, on);
      this.postfx?.setEffectEnabled(name, on);
    }
  }

  /**
   * Toggle one post effect for the current mood only. No-op when
   * post-processing is unavailable.
   *
   * **This does not stick.** The next `setMood()` calls `postfx.applyPreset()`,
   * which swaps the whole settings tree and discards anything set here. Both
   * scenes call `setMood` in `enter()`, so a toggle made outside a scene lasts
   * until the first route change and then vanishes with no error — the exact
   * failure that made an earlier interior-ink fix look like it had landed.
   * Use `forceEffect` for any decision that must outlive a mood change.
   */
  setEffectEnabled(name: EffectName, on: boolean): void {
    this.postfx?.setEffectEnabled(name, on);
  }

  /**
   * Register an object (and its subtree) as a bloom source.
   *
   * Usually unnecessary: the post stack re-scans the scene twice a second and
   * picks up anything with a non-black emissive, or `userData.glow = true`.
   * Use this when you want a non-emissive object to glow anyway.
   */
  addGlow(object: Object3D): void {
    this.postfx?.addGlow(object);
  }

  /** Stop an object contributing to bloom. */
  removeGlow(object: Object3D): void {
    this.postfx?.removeGlow(object);
  }

  /* ------------------------------------------------------------------ *
   * Frame loop
   * ------------------------------------------------------------------ */

  onUpdate(fn: (dt: number, elapsed: number) => void): () => void {
    this.updaters.add(fn);
    return () => this.updaters.delete(fn);
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.clock.reset();
    const tick = () => {
      if (!this.running) return;
      this.rafId = requestAnimationFrame(tick);
      // Clamp dt so an alt-tab pause can't teleport physics bodies through walls.
      this.clock.update();
      const dt = Math.min(this.clock.getDelta(), 1 / 20);
      const elapsed = this.clock.getElapsed();

      const t0 = performance.now();
      this.renderer.info.reset();
      for (const fn of this.updaters) fn(dt, elapsed);
      this.applyCameraOrientation();
      this.renderFrame(dt);
      const t1 = performance.now();

      this.frameStats(dt, t1 - t0);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  /** One frame, with a graceful fall-back if the composer misbehaves. */
  private renderFrame(dt: number): void {
    if (this.postfx && this.postfx.render(dt)) return;
    // Direct path. The composer switches `autoClear` off, so clear explicitly:
    // otherwise the fall-back inherits a stale frame and ghosts.
    this.renderer.setRenderTarget(null);
    this.renderer.clear();
    this.renderer.render(this.scene, this.camera);
  }

  private frameStats(dt: number, frameMs: number): void {
    this.watchdog(frameMs);
    this.fpsAccum += dt;
    this.fpsFrames++;
    if (this.fpsAccum >= 0.5) {
      this.stats.fps = Math.round(this.fpsFrames / this.fpsAccum);
      this.stats.frameMs = Math.round(frameMs * 100) / 100;
      this.stats.drawCalls = this.renderer.info.render.calls;
      this.stats.triangles = this.renderer.info.render.triangles;
      this.fpsAccum = 0;
      this.fpsFrames = 0;
    }
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.rafId);
  }

  dispose(): void {
    this.stop();
    this.resizeObserver?.disconnect();
    if (this.windowResizeHandler) {
      window.removeEventListener('resize', this.windowResizeHandler);
      this.windowResizeHandler = undefined;
    }
    this.updaters.clear();
    this.removeOutlineTestRig();
    this.postfx?.dispose();
    this.lighting.dispose();
    this.renderer.dispose();
  }

  /* ------------------------------------------------------------------ *
   * Misc scene controls
   * ------------------------------------------------------------------ */

  /** Opt into filmic tone mapping — used only by cinematic result screens. */
  setCinematic(on: boolean): void {
    this.renderer.toneMapping = on ? ACESFilmicToneMapping : NoToneMapping;
  }

  /** Distance fog, tinted to the shell green so far rooms recede softly. */
  setFog(near: number, far: number, color = 0x173721): void {
    this.scene.fog = new Fog(srgb(color), near, far);
  }

  clearFog(): void {
    this.scene.fog = null;
  }

  /* ------------------------------------------------------------------ *
   * Outline verification rig (?debug=outline)
   * ------------------------------------------------------------------ */

  /**
   * Drop a row of hard-edged, thin, and open-geometry test shapes into the
   * scene. This is the fixture the inverted-hull fixes in `cel.ts` are verified
   * against: every shape here broke at least one of the old code paths.
   */
  addOutlineTestRig(): Group {
    this.removeOutlineTestRig();
    const rig = makeOutlineTestRig();
    this.scene.add(rig);
    this.debugRig = rig;
    return rig;
  }

  removeOutlineTestRig(): void {
    if (!this.debugRig) return;
    this.scene.remove(this.debugRig);
    disposeTree(this.debugRig);
    this.debugRig.clear();
    this.debugRig = undefined;
  }
}

/** Read the escape-hatch flags. Tolerates a missing `location` (SSR, tests). */
function readQuery(): { fx?: string; shadows?: string; debug?: string; quality?: string } {
  try {
    const params = new URLSearchParams(window.location.search);
    const out: { fx?: string; shadows?: string; debug?: string; quality?: string } = {};
    const fx = params.get('fx');
    const shadows = params.get('shadows');
    const debug = params.get('debug');
    const quality = params.get('quality');
    if (fx) out.fx = fx;
    if (shadows) out.shadows = shadows;
    if (debug) out.debug = debug;
    if (quality) out.quality = quality.toLowerCase();
    return out;
  } catch {
    return {};
  }
}
